import React, { useState, useMemo } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const HttpStatusCheckerPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // HTTP Status Codes data
  const statusCodes = [
    // 1xx Informational
    { code: 100, name: 'Continue', category: 'informational', description: 'The server has received the request headers and the client should proceed to send the request body.' },
    { code: 101, name: 'Switching Protocols', category: 'informational', description: 'The requester has asked the server to switch protocols and the server has agreed to do so.' },
    { code: 102, name: 'Processing', category: 'informational', description: 'The server has received and is processing the request, but no response is available yet.' },
    { code: 103, name: 'Early Hints', category: 'informational', description: 'Used to return some response headers before final HTTP message.' },
    
    // 2xx Success
    { code: 200, name: 'OK', category: 'success', description: 'The request has succeeded. The meaning of the success depends on the HTTP method.' },
    { code: 201, name: 'Created', category: 'success', description: 'The request has been fulfilled and resulted in a new resource being created.' },
    { code: 202, name: 'Accepted', category: 'success', description: 'The request has been accepted for processing, but the processing has not been completed.' },
    { code: 203, name: 'Non-Authoritative Information', category: 'success', description: 'The server is a transforming proxy that received a 200 OK from its origin, but is returning a modified version.' },
    { code: 204, name: 'No Content', category: 'success', description: 'The server successfully processed the request and is not returning any content.' },
    { code: 205, name: 'Reset Content', category: 'success', description: 'The server successfully processed the request, asks that the requester reset its document view, and is not returning any content.' },
    { code: 206, name: 'Partial Content', category: 'success', description: 'The server is delivering only part of the resource due to a range header sent by the client.' },
    { code: 207, name: 'Multi-Status', category: 'success', description: 'The message body that follows is by default an XML message and can contain a number of separate response codes.' },
    { code: 208, name: 'Already Reported', category: 'success', description: 'The members of a DAV binding have already been enumerated in a preceding part of the multi-status response.' },
    { code: 226, name: 'IM Used', category: 'success', description: 'The server has fulfilled a request for the resource, and the response is a representation of the result of one or more instance-manipulations.' },
    
    // 3xx Redirection
    { code: 300, name: 'Multiple Choices', category: 'redirection', description: 'Indicates multiple options for the resource from which the client may choose.' },
    { code: 301, name: 'Moved Permanently', category: 'redirection', description: 'This and all future requests should be directed to the given URI.' },
    { code: 302, name: 'Found', category: 'redirection', description: 'Tells the client to look at (browse to) another URL.' },
    { code: 303, name: 'See Other', category: 'redirection', description: 'The response to the request can be found under another URI using the GET method.' },
    { code: 304, name: 'Not Modified', category: 'redirection', description: 'Indicates that the resource has not been modified since the version specified by the request headers.' },
    { code: 305, name: 'Use Proxy', category: 'redirection', description: 'The requested resource is available only through a proxy, the address for which is provided in the response.' },
    { code: 306, name: 'Switch Proxy', category: 'redirection', description: 'No longer used. Originally meant "Subsequent requests should use the specified proxy."' },
    { code: 307, name: 'Temporary Redirect', category: 'redirection', description: 'The request should be repeated with another URI; however, future requests should still use the original URI.' },
    { code: 308, name: 'Permanent Redirect', category: 'redirection', description: 'The request and all future requests should be repeated using another URI.' },
    
    // 4xx Client Error
    { code: 400, name: 'Bad Request', category: 'client-error', description: 'The server cannot or will not process the request due to an apparent client error.' },
    { code: 401, name: 'Unauthorized', category: 'client-error', description: 'Similar to 403 Forbidden, but specifically for use when authentication is required and has failed or has not yet been provided.' },
    { code: 402, name: 'Payment Required', category: 'client-error', description: 'Reserved for future use. The original intention was that this code might be used as part of some form of digital cash or micropayment scheme.' },
    { code: 403, name: 'Forbidden', category: 'client-error', description: 'The request was valid, but the server is refusing action.' },
    { code: 404, name: 'Not Found', category: 'client-error', description: 'The requested resource could not be found but may be available in the future.' },
    { code: 405, name: 'Method Not Allowed', category: 'client-error', description: 'A request method is not supported for the requested resource.' },
    { code: 406, name: 'Not Acceptable', category: 'client-error', description: 'The requested resource is capable of generating only content not acceptable according to the Accept headers sent in the request.' },
    { code: 407, name: 'Proxy Authentication Required', category: 'client-error', description: 'The client must first authenticate itself with the proxy.' },
    { code: 408, name: 'Request Timeout', category: 'client-error', description: 'The server timed out waiting for the request.' },
    { code: 409, name: 'Conflict', category: 'client-error', description: 'Indicates that the request could not be processed because of conflict in the request.' },
    { code: 410, name: 'Gone', category: 'client-error', description: 'Indicates that the resource requested is no longer available and will not be available again.' },
    { code: 411, name: 'Length Required', category: 'client-error', description: 'The request did not specify the length of its content, which is required by the requested resource.' },
    { code: 412, name: 'Precondition Failed', category: 'client-error', description: 'The server does not meet one of the preconditions that the requester put on the request header fields.' },
    { code: 413, name: 'Payload Too Large', category: 'client-error', description: 'The request is larger than the server is willing or able to process.' },
    { code: 414, name: 'URI Too Long', category: 'client-error', description: 'The URI provided was too long for the server to process.' },
    { code: 415, name: 'Unsupported Media Type', category: 'client-error', description: 'The request entity has a media type which the server or resource does not support.' },
    { code: 416, name: 'Range Not Satisfiable', category: 'client-error', description: 'The client has asked for a portion of the file, but the server cannot supply that portion.' },
    { code: 417, name: 'Expectation Failed', category: 'client-error', description: 'The server cannot meet the requirements of the Expect request-header field.' },
    { code: 418, name: "I'm a teapot", category: 'client-error', description: 'This code was defined in 1998 as one of the traditional IETF April Fools\' jokes, in RFC 2324.' },
    { code: 421, name: 'Misdirected Request', category: 'client-error', description: 'The request was directed at a server that is not able to produce a response.' },
    { code: 422, name: 'Unprocessable Entity', category: 'client-error', description: 'The request was well-formed but was unable to be followed due to semantic errors.' },
    { code: 423, name: 'Locked', category: 'client-error', description: 'The resource that is being accessed is locked.' },
    { code: 424, name: 'Failed Dependency', category: 'client-error', description: 'The request failed because it depended on another request and that request failed.' },
    { code: 425, name: 'Too Early', category: 'client-error', description: 'Indicates that the server is unwilling to risk processing a request that might be replayed.' },
    { code: 426, name: 'Upgrade Required', category: 'client-error', description: 'The client should switch to a different protocol such as TLS/1.3, given in the Upgrade header field.' },
    { code: 428, name: 'Precondition Required', category: 'client-error', description: 'The origin server requires the request to be conditional.' },
    { code: 429, name: 'Too Many Requests', category: 'client-error', description: 'The user has sent too many requests in a given amount of time.' },
    { code: 431, name: 'Request Header Fields Too Large', category: 'client-error', description: 'The server is unwilling to process the request because either an individual header field, or all the header fields collectively, are too large.' },
    { code: 451, name: 'Unavailable For Legal Reasons', category: 'client-error', description: 'A server operator has received a legal demand to deny access to a resource or to a set of resources.' },
    
    // 5xx Server Error
    { code: 500, name: 'Internal Server Error', category: 'server-error', description: 'A generic error message, given when an unexpected condition was encountered and no more specific message is suitable.' },
    { code: 501, name: 'Not Implemented', category: 'server-error', description: 'The server either does not recognize the request method, or it lacks the ability to fulfill the request.' },
    { code: 502, name: 'Bad Gateway', category: 'server-error', description: 'The server was acting as a gateway or proxy and received an invalid response from the upstream server.' },
    { code: 503, name: 'Service Unavailable', category: 'server-error', description: 'The server is currently unavailable (because it is overloaded or down for maintenance).' },
    { code: 504, name: 'Gateway Timeout', category: 'server-error', description: 'The server was acting as a gateway or proxy and did not receive a timely response from the upstream server.' },
    { code: 505, name: 'HTTP Version Not Supported', category: 'server-error', description: 'The server does not support the HTTP protocol version used in the request.' },
    { code: 506, name: 'Variant Also Negotiates', category: 'server-error', description: 'Transparent content negotiation for the request results in a circular reference.' },
    { code: 507, name: 'Insufficient Storage', category: 'server-error', description: 'The server is unable to store the representation needed to complete the request.' },
    { code: 508, name: 'Loop Detected', category: 'server-error', description: 'The server detected an infinite loop while processing the request.' },
    { code: 510, name: 'Not Extended', category: 'server-error', description: 'Further extensions to the request are required for the server to fulfill it.' },
    { code: 511, name: 'Network Authentication Required', category: 'server-error', description: 'The client needs to authenticate to gain network access.' }
  ];

  const categories = [
    { value: 'all', label: 'All Status Codes', color: '#6c757d' },
    { value: 'informational', label: '1xx Informational', color: '#17a2b8' },
    { value: 'success', label: '2xx Success', color: '#28a745' },
    { value: 'redirection', label: '3xx Redirection', color: '#ffc107' },
    { value: 'client-error', label: '4xx Client Error', color: '#fd7e14' },
    { value: 'server-error', label: '5xx Server Error', color: '#dc3545' }
  ];

  // Filter status codes based on search and category
  const filteredStatusCodes = useMemo(() => {
    return statusCodes.filter(status => {
      const matchesSearch = searchTerm === '' || 
        status.code.toString().includes(searchTerm) ||
        status.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        status.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || status.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const getCategoryColor = (category) => {
    const categoryObj = categories.find(cat => cat.value === category);
    return categoryObj ? categoryObj.color : '#6c757d';
  };

  const getCategoryBadge = (category) => {
    switch(category) {
      case 'informational': return '1xx';
      case 'success': return '2xx';
      case 'redirection': return '3xx';
      case 'client-error': return '4xx';
      case 'server-error': return '5xx';
      default: return '';
    }
  };

  return (
    <div>
      <SEO 
        title="HTTP Status Code Checker - Complete Reference Guide" 
        description="Complete HTTP status code reference with search functionality. Find meanings and descriptions for all HTTP status codes from 1xx to 5xx."
        keywords="http status codes, http response codes, 404 error, 500 error, status code reference, web development"
      />
      
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>HTTP Status Code Checker</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Complete reference for HTTP status codes with search and filtering capabilities.
        </p>
        

        <div className="main-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Search and Filter Controls */}
          <div className="controls-section" style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '20px' }}>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search by code, name, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div className="category-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {categories.map(category => (
                <button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  style={{
                    padding: '8px 16px',
                    border: selectedCategory === category.value ? `2px solid ${category.color}` : '1px solid #ddd',
                    borderRadius: '20px',
                    backgroundColor: selectedCategory === category.value ? category.color : 'white',
                    color: selectedCategory === category.value ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: selectedCategory === category.value ? 'bold' : 'normal',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          {/* Results Count */}
          <div style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
            Showing {filteredStatusCodes.length} status code{filteredStatusCodes.length !== 1 ? 's' : ''}
          </div>

          {/* Status Codes Grid */}
          <div className="status-codes-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
            {filteredStatusCodes.map(status => (
              <div 
                key={status.code}
                className="status-card"
                style={{
                  border: `2px solid ${getCategoryColor(status.category)}`,
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                <div className="status-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div className="status-code" style={{ fontSize: '24px', fontWeight: 'bold', color: getCategoryColor(status.category) }}>
                    {status.code}
                  </div>
                  <div 
                    className="category-badge"
                    style={{
                      backgroundColor: getCategoryColor(status.category),
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    {getCategoryBadge(status.category)}
                  </div>
                </div>
                
                <div className="status-name" style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                  {status.name}
                </div>
                
                <div className="status-description" style={{ fontSize: '14px', lineHeight: '1.5', color: '#666' }}>
                  {status.description}
                </div>
              </div>
            ))}
          </div>

          {filteredStatusCodes.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <h3>No status codes found</h3>
              <p>Try adjusting your search terms or category filter.</p>
            </div>
          )}
        </div>
        
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>About HTTP Status Codes</h2>
          <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
            HTTP status codes are three-digit numbers returned by web servers to indicate the result of a client's request.
            They are grouped into five categories based on the first digit.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginTop: '20px' }}>
            {categories.slice(1).map(category => (
              <div key={category.value} style={{ padding: '15px', border: `1px solid ${category.color}`, borderRadius: '8px' }}>
                <h4 style={{ color: category.color, margin: '0 0 8px 0' }}>{category.label}</h4>
                <p style={{ fontSize: '14px', color: '#666', margin: 0, lineHeight: '1.4' }}>
                  {category.value === 'informational' && 'Request received, continuing process'}
                  {category.value === 'success' && 'Request successfully received, understood, and accepted'}
                  {category.value === 'redirection' && 'Further action must be taken to complete the request'}
                  {category.value === 'client-error' && 'Request contains bad syntax or cannot be fulfilled'}
                  {category.value === 'server-error' && 'Server failed to fulfill an apparently valid request'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .status-codes-grid {
            grid-template-columns: 1fr !important;
          }
          
          .category-filters {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default HttpStatusCheckerPage; 